import { useState, type FormEvent } from 'react';
import { ApiError, api } from '@/lib/api';
import { t } from '@/lib/i18n';
import { Button, ErrorBanner, Field, Input, Notice } from './ui';

export function ChangePasswordForm({ onDone }: { onDone?: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const fe = error instanceof ApiError ? error.fieldErrors() : {};
  const mismatch = confirm.length > 0 && next !== confirm;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (mismatch) return;
    setLoading(true);
    setError(null);
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
      onDone?.();
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      {done && <Notice tone="green">{t('Password changed. Other devices have been signed out.')}</Notice>}
      <ErrorBanner error={error && !Object.keys(fe).length ? error : null} />
      <Field label={t('Current password')} error={fe.currentPassword && t(fe.currentPassword)}>
        <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} invalid={!!fe.currentPassword} />
      </Field>
      <Field
        label={t('New password')}
        error={fe.newPassword && t(fe.newPassword)}
        hint={t('At least 10 characters, using three of: lower-case, upper-case, digits, symbols.')}
      >
        <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} invalid={!!fe.newPassword} />
      </Field>
      <Field label={t('Confirm new password')} error={mismatch ? t('Passwords do not match') : undefined}>
        <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} invalid={mismatch} />
      </Field>
      <Button type="submit" loading={loading} disabled={!current || !next || next !== confirm}>
        {t('Change password')}
      </Button>
    </form>
  );
}
